import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, CircularProgress } from "@mui/material";

// Eagerly loaded — Navbar is on every page so lazy-loading it would
// flash. AuthProvider has to wrap everything.
import Navbar from "./components/Navbar";
import { AuthProvider } from "./contexts/AuthContext";
import { appTheme } from "./theme";

// Lazy-loaded pages. React.lazy() splits each route into its own chunk
// so the initial bundle drops from ~1.2 MB to ~250 KB. The Suspense
// fallback is a centred spinner; routes load in <500ms on a fast
// connection (chunks are cached on subsequent navigations).
const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/auth/Login"));
const Register = lazy(() => import("./pages/auth/Register"));
const Verify = lazy(() => import("./pages/auth/Verify"));
const Logout = lazy(() => import("./pages/auth/Logout"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const AccountSettings = lazy(() => import("./pages/auth/AccountSettings"));
const QRCodeGenerator = lazy(() => import("./pages/QRCodeGenerator"));
const ImageTrim = lazy(() => import("./pages/ImageTrim"));
const ImageResizer = lazy(() => import("./pages/ImageResizer"));
const FontSelector = lazy(() => import("./pages/FontSelector"));
const SignMaker = lazy(() => import("./pages/SignMaker"));
const ProjectMatrix = lazy(() => import("./pages/ProjectMatrix"));
const SecurityTools = lazy(() => import("./pages/SecurityTools"));
const PromptGenerator = lazy(() => import("./pages/PromptGenerator"));
const PromptLibrary = lazy(() => import("./pages/PromptLibrary"));
const Qbert = lazy(() => import("./pages/Qbert"));
const CertSprint = lazy(() => import("./pages/CertSprint"));
const WebStats = lazy(() => import("./pages/WebStats"));
const ScriptDownloader = lazy(() => import("./pages/ScriptDownloader"));
const RecipeFormatter = lazy(() => import("./pages/RecipeFormatter"));
const ExpenseTracker = lazy(() => import("./pages/ExpenseTracker"));
const JsonLinter = lazy(() => import("./pages/JsonLinter"));
const MaintenanceTracker = lazy(() => import("./pages/MaintenanceTracker"));
const AppDownloads = lazy(() => import("./pages/AppDownloads"));

const RouteFallback: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      py: 8,
    }}
    data-testid="route-loading"
  >
    <CircularProgress aria-label="Loading page" />
  </Box>
);

function App() {
  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Navbar />
          {/* Single <main> landmark for every routed page (a11y:
              landmark-one-main + region). Qbert's sidebar renders as a
              labelled <aside> inside it. v51. */}
          <main>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route
                  path="/qr-code-generator"
                  element={<QRCodeGenerator />}
                />
                <Route path="/image-trim" element={<ImageTrim />} />
                <Route path="/image-resizer" element={<ImageResizer />} />
                <Route path="/font-selector" element={<FontSelector />} />
                <Route path="/sign-maker" element={<SignMaker />} />
                <Route path="/project-matrix" element={<ProjectMatrix />} />
                <Route path="/security-tools" element={<SecurityTools />} />
                <Route path="/prompt-generator" element={<PromptGenerator />} />
                <Route path="/prompt-library" element={<PromptLibrary />} />
                <Route path="/qbert" element={<Qbert />} />
                <Route path="/cert-sprint" element={<CertSprint />} />
                <Route path="/apps" element={<AppDownloads />} />
                <Route path="/auth/login" element={<Login />} />
                <Route path="/auth/register" element={<Register />} />
                <Route
                  path="/auth/forgot-password"
                  element={<ForgotPassword />}
                />
                <Route path="/auth/verify" element={<Verify />} />
                <Route path="/auth/logout" element={<Logout />} />
                <Route path="/account" element={<AccountSettings />} />
                <Route path="/web-stats" element={<WebStats />} />
                <Route
                  path="/script-downloader"
                  element={<ScriptDownloader />}
                />
                <Route path="/recipe-formatter" element={<RecipeFormatter />} />
                <Route path="/expense-tracker" element={<ExpenseTracker />} />
                <Route
                  path="/expense-tracker/:trackerId"
                  element={<ExpenseTracker />}
                />
                <Route path="/json-linter" element={<JsonLinter />} />
                <Route
                  path="/maintenance-tracker"
                  element={<MaintenanceTracker />}
                />
              </Routes>
            </Suspense>
          </main>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
